import type { TemplateType } from '~/types/template';

import { usePageAnalyticFilterStore, usePageAnalyticStore } from '../stores';
import { useMetricsSelectorStore } from '../stores/metrics-selector';
import { useFetchPageAnalyticsV2 } from './useFetchPageAnalyticsV2';
import { useDebouncedEffect } from '~/hooks/useDebouncedEffect';
import { useMetricsTimeFilter } from '~/modules/analytics';
import { useGemXQlPagination } from '~/core/gemxql/hooks';
import { useDateTimeFilter } from './useDateTimeFilter';
import { useFeatureFlagChecker } from '~/modules/apps';
import { getPageTitle } from '../helpers';
import { useMemo, useState } from 'react';

interface IUseInitializePageAnalyticProps {
  isQueryPreviousData?: boolean;
  isSaveDateTimeToLocalStorage?: boolean;
  sortSelected?: string[];
  queryValue?: string;
  filterPageTypes?: TemplateType[];
}

const SEARCH_DEBOUNCE_MS = 500;
const ITEMS_PER_PAGE = 20;

export const useInitializePageAnalytic = (props?: IUseInitializePageAnalyticProps) => {
  const analyticMode = usePageAnalyticFilterStore((state) => state.analyticMode);
  const pagesInfo = usePageAnalyticStore((state) => state.pagesInfo);
  const selectedMetrics = useMetricsSelectorStore((state) => state.selectedMetrics);

  const {
    isQueryPreviousData = true,
    isSaveDateTimeToLocalStorage,
    sortSelected,
    queryValue,
    filterPageTypes,
  } = props || {};
  useDateTimeFilter(isSaveDateTimeToLocalStorage);
  const { currentDates, previousDates } = useMetricsTimeFilter();
  const { isEnabledFilterByShopifyPageId } = useFeatureFlagChecker();

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(queryValue);

  const paginationResetKey = useMemo(
    () => ({
      analyticMode,
      currentDates,
      debouncedSearchValue,
      filterPageTypes,
      selectedMetrics,
      sortSelected,
    }),
    [
      analyticMode,
      currentDates,
      debouncedSearchValue,
      filterPageTypes,
      selectedMetrics,
      sortSelected,
    ],
  );

  const { offset, buildPagination } = useGemXQlPagination({
    resetKey: paginationResetKey,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const fetchPageAnalytic = useFetchPageAnalyticsV2({
    currentDates,
    previousDates,
    isQueryPreviousData,
    mode: analyticMode,
    sortSelected,
    searchValue: debouncedSearchValue,
    filterPageTypes,
    limit: ITEMS_PER_PAGE,
    offset,
  });

  const { data, totalRecords } = fetchPageAnalytic;

  const { currentPage, totalPages, handlePageChange } = buildPagination({
    totalRecords,
  });

  const listPageStartSession = useMemo(() => {
    if (!isEnabledFilterByShopifyPageId) return data;

    return data?.map((item) => {
      const { shopify_page_id, page_type } = item.pageItems;
      const shopifyPageInfo = pagesInfo[shopify_page_id];
      return {
        ...item,
        pageItems: {
          ...item.pageItems,
          page_title: getPageTitle({
            shopifyPageType: page_type as TemplateType,
            pageInfo: shopifyPageInfo,
          }),
        },
      };
    });
  }, [data, pagesInfo, isEnabledFilterByShopifyPageId]);

  useDebouncedEffect(() => setDebouncedSearchValue(queryValue), [queryValue], SEARCH_DEBOUNCE_MS);

  return {
    listPageStartSession,
    currentPage,
    totalPages,
    itemsPerPage: ITEMS_PER_PAGE,
    handlePageChange,
    ...fetchPageAnalytic,
  };
};

export type IInitializePageAnalytic = ReturnType<typeof useInitializePageAnalytic>;
